import { db, storage } from '../firebase/client';
import { 
  collection, doc, getDoc, getDocs, addDoc, updateDoc, 
  deleteDoc, query, orderBy, where, serverTimestamp, limit 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import Service from '../models/Service';
import { cacheService, CACHE_KEYS } from './cacheService';

const COLLECTION_NAME = 'services';

const clearServiceCaches = () => {
  cacheService.clear();
};

/**
 * Get all services
 * @param {boolean} activeOnly - If true, only return active services
 * @param {number} limitCount - Limit the number of services returned
 * @returns {Promise<Array<Service>>} Array of service objects
 */
export const getAllServices = async (activeOnly = false, limitCount = 0) => {
  // Check cache first
  const cachedServices = cacheService.get(CACHE_KEYS.ALL_SERVICES);
  if (cachedServices) {
    return activeOnly ? cachedServices.filter(service => service.active) : cachedServices;
  }

  try {
    console.log('Fetching all services, activeOnly:', activeOnly, 'limitCount:', limitCount);
    
    // Avoid compound queries that require indexes
    // Instead, get all services and filter client-side
    const servicesRef = collection(db, COLLECTION_NAME);
    const querySnapshot = await getDocs(servicesRef);
    
    // Map to Service objects
    let services = querySnapshot.docs.map(doc => 
      Service.fromFirestore(doc.id, doc.data())
    );
    
    // Apply filters client-side to avoid index requirements
    if (activeOnly) {
      services = services.filter(service => service.active === true);
    }
    
    // Sort by createdAt if available
    services.sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt - a.createdAt;
    });
    
    // Apply limit if needed
    if (limitCount > 0 && services.length > limitCount) {
      services = services.slice(0, limitCount);
    }
    
    console.log(`Fetched ${services.length} services${activeOnly ? ' (active only)' : ''}`);

    // Cache the results
    cacheService.set(CACHE_KEYS.ALL_SERVICES, services);
    
    return services;
  } catch (error) {
    console.error('Error getting services:', error);
    throw error;
  }
};

/**
 * Get services by category ID
 * @param {string} categoryId - The category ID to filter by
 * @param {boolean} activeOnly - Whether to only return active services
 * @returns {Promise<Array>} - Array of services
 */
export const getServicesByCategory = async (categoryId, activeOnly = true) => {
  const cacheKey = `${CACHE_KEYS.CATEGORY_SERVICES}_${categoryId}`;
  const cachedServices = cacheService.get(cacheKey);
  if (cachedServices) {
    return activeOnly ? cachedServices.filter(service => service.active) : cachedServices;
  }

  try {
    console.log(`Fetching services for category ID: ${categoryId}`);
    
    // Use simpler query to avoid index requirements
    const servicesCollection = collection(db, 'services');
    
    // First get all services
    const snapshot = await getDocs(servicesCollection);
    
    // Then filter client-side
    let services = snapshot.docs
      .map(doc => Service.fromFirestore(doc.id, doc.data()))
      // Filter by categoryId
      .filter(service => service.categoryId === categoryId);
    
    // Apply active filter if needed
    if (activeOnly) {
      services = services.filter(service => service.active === true);
    }
    
    console.log(`Found ${services.length} services for category ID: ${categoryId}`);
    services.forEach(service => {
      console.log(`Service: ${service.name}, Category ID: ${service.categoryId}`);
    });

    // Cache the results
    cacheService.set(cacheKey, services);
    
    return services;
  } catch (error) {
    console.error('Error fetching services by category:', error);
    throw error;
  }
};

/**
 * Get services by subcategory ID or name
 * @param {string} categoryId - The category ID
 * @param {string} subcategoryId - The subcategory ID or name
 * @param {boolean} activeOnly - Whether to only return active services
 * @returns {Promise<Array>} - Array of services
 */
export const getServicesBySubcategory = async (categoryId, subcategoryId, activeOnly = true) => {
  try {
    console.log(`Fetching services for category ID: ${categoryId} and subcategory ID/name: ${subcategoryId}`);
    
    // Use simpler query to avoid index requirements
    const servicesCollection = collection(db, 'services');
    
    // Get all services first
    const snapshot = await getDocs(servicesCollection);
    let allServices = snapshot.docs.map(doc => Service.fromFirestore(doc.id, doc.data()));
    
    // Apply filters client-side
    // First filter by active status if needed
    if (activeOnly) {
      allServices = allServices.filter(service => service.active === true);
    }
    
    // Then filter by categoryId
    const categoryServices = allServices.filter(service => service.categoryId === categoryId);
    
    // First try using subcategoryId directly
    let services = categoryServices.filter(service => service.subcategoryId === subcategoryId);
    
    // If no results, try using subcategory name
    if (services.length === 0) {
      console.log('No results with subcategoryId, trying with subcategory name');
      services = categoryServices.filter(service => service.subcategory === subcategoryId);
    }
    
    // If still no results and the subcategoryId might be a UUID, try matching subcategory by name pattern
    if (services.length === 0 && subcategoryId && subcategoryId.includes('-')) {
      console.log('No results, subcategoryId looks like a UUID, trying to match by name pattern');
      
      // Get category data to find subcategory name
      const categoryDoc = await getDoc(doc(db, 'categories', categoryId));
      if (categoryDoc.exists()) {
        const categoryData = categoryDoc.data();
        if (categoryData.subcategories && Array.isArray(categoryData.subcategories)) {
          const matchingSubcategory = categoryData.subcategories.find(sub => 
            (sub.id === subcategoryId) || 
            (typeof sub === 'object' && sub.id === subcategoryId)
          );
          
          if (matchingSubcategory) {
            const subcategoryName = typeof matchingSubcategory === 'string' ? 
              matchingSubcategory : matchingSubcategory.name;
              
            console.log(`Found matching subcategory name: ${subcategoryName} for ID: ${subcategoryId}`);
            
            // Filter services by subcategory name
            services = categoryServices.filter(service => 
              service.subcategory === subcategoryName || 
              service.subcategoryId === subcategoryId
            );
          }
        }
      }
    }
    
    console.log(`Found ${services.length} services for subcategory ID/name: ${subcategoryId}`);
    
    if (services.length > 0) {
      services.forEach(service => {
        console.log(`Service: ${service.name}, Category ID: ${service.categoryId}, Subcategory ID: ${service.subcategoryId}, Subcategory: ${service.subcategory}`);
      });
    } else {
      console.log(`No services found for subcategory: ${subcategoryId}`);
    }
    
    return services;
  } catch (error) {
    console.error('Error fetching services by subcategory:', error);
    throw error;
  }
};

/**
 * Get a service by ID
 * @param {string} serviceId - Service ID
 * @returns {Promise<Service|null>} Service object or null if not found
 */
export const getServiceById = async (serviceId) => {
  try {
    const serviceDoc = await getDoc(doc(db, 'services', serviceId));
    if (serviceDoc.exists()) {
      return {
        id: serviceDoc.id,
        ...serviceDoc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting service:', error);
    throw error;
  }
};

/**
 * Create a new service
 * @param {Object} serviceData - Service data
 * @returns {Promise<string>} New service ID
 */
export const createService = async (serviceData) => {
  try {
    // Add timestamps
    const timestamp = serverTimestamp();
    serviceData.createdAt = timestamp;
    serviceData.updatedAt = timestamp;
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), serviceData);
    clearServiceCaches();
    return docRef.id;
  } catch (error) {
    console.error('Error creating service:', error);
    throw error;
  }
};

/**
 * Update an existing service
 * @param {string} serviceId - Service ID to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateService = async (serviceId, updates) => {
  try {
    const serviceRef = doc(db, COLLECTION_NAME, serviceId);
    
    // Update timestamp
    updates.updatedAt = serverTimestamp();
    
    await updateDoc(serviceRef, updates);
    clearServiceCaches();
  } catch (error) {
    console.error('Error updating service:', error);
    throw error;
  }
};

/**
 * Delete a service
 * @param {string} serviceId - Service ID to delete
 * @returns {Promise<void>}
 */
export const deleteService = async (serviceId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, serviceId));
    clearServiceCaches();
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error;
  }
};

/**
 * Upload a service image
 * @param {File} imageFile - Image file to upload
 * @param {string} serviceId - Service ID
 * @returns {Promise<string>} Image URL
 */
export const uploadServiceImage = async (imageFile, serviceId) => {
  try {
    const fileName = `${serviceId || uuidv4()}_${Date.now()}_${imageFile.name}`;
    const fileRef = ref(storage, `services/${fileName}`);
    
    await uploadBytes(fileRef, imageFile);
    const imageUrl = await getDownloadURL(fileRef);
    
    return imageUrl;
  } catch (error) {
    console.error('Error uploading service image:', error);
    throw error;
  }
};

/**
 * Delete a service image
 * @param {string} imageUrl - URL of the image to delete
 * @returns {Promise<void>}
 */
export const deleteServiceImage = async (imageUrl) => {
  try {
    // Extract the file path from the URL
    const fileRef = ref(storage, imageUrl);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting service image:', error);
    throw error;
  }
};

/**
 * Get popular services
 * @param {number} count - Number of services to return
 * @returns {Promise<Array<Service>>} Array of service objects
 */
export const getPopularServices = async (count = 6) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('active', '==', true),
      where('popular', '==', true),
      limit(count)
    );
    
    const querySnapshot = await getDocs(q);
    
    const services = querySnapshot.docs.map(doc => 
      Service.fromFirestore(doc.id, doc.data())
    );
    
    return services;
  } catch (error) {
    console.error('Error getting popular services:', error);
    throw error;
  }
};

export default {
  getAllServices,
  getServicesByCategory,
  getServicesBySubcategory,
  getServiceById,
  createService,
  updateService,
  deleteService,
  uploadServiceImage,
  deleteServiceImage,
  getPopularServices
}; 